import type {
  APIShareRegionalTrendResponse,
  APISharedProfileResponse,
  TechName,
} from '@see-stella/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BrandMark } from '../../components/BrandMark'
import { getProfile, getRegionalTrend, ShareApiError } from '../../lib/shareApi'
import { displayName, formatScanRange, plural } from './format'
import { ChangeLogSection } from './ChangeLogSection'
import { MetricDefinition } from './MetricDefinition'
import { MetricSwitcher } from './MetricSwitcher'
import { ProfileNotice } from './ProfileNotice'
import { regionalByScanId } from './regional'
import { RoutineSection } from './RoutineSection'
import { ScanPlayer } from './ScanPlayer'
import { useRegionalTrend } from './useRegionalTrend'
import { sectionVisibility } from './visibility'
import { useReportState } from './useReportState'
import './ProfileScreen.css'

interface ProfileScreenProps {
  viewToken: string
  onSessionExpired: () => void
  /** Fired once the profile loads, so the host can reset its re-open guard. */
  onLoaded?: () => void
}

type Load =
  | { status: 'loading' }
  | { status: 'ready'; profile: APISharedProfileResponse }
  | { status: 'error'; reason: ShareApiError['reason'] }

export function ProfileScreen({
  viewToken,
  onSessionExpired,
  onLoaded,
}: ProfileScreenProps) {
  const [load, setLoad] = useState<Load>({ status: 'loading' })

  useEffect(() => {
    let live = true
    // Reset to the spinner on a re-open (new viewToken) so stale content from the
    // prior session never lingers behind the fresh fetch.
    setLoad({ status: 'loading' })
    getProfile(viewToken)
      .then((profile) => {
        if (!live) return
        setLoad({ status: 'ready', profile })
        onLoaded?.()
      })
      .catch((err) => {
        if (!live) return
        const reason = err instanceof ShareApiError ? err.reason : 'unknown'
        if (reason === 'session_expired') onSessionExpired()
        else setLoad({ status: 'error', reason })
      })
    return () => {
      live = false
    }
  }, [viewToken, onSessionExpired, onLoaded])

  // The recipient fetches region trends over the view token (public endpoint).
  // Injected into ReportView so the owner preview can swap in its authed fetcher
  // and reuse the identical render path. (Declared before the early returns so the
  // hook order stays stable.)
  const fetchRegionalTrend = useCallback(
    (techName: TechName): Promise<APIShareRegionalTrendResponse> =>
      getRegionalTrend(viewToken, techName),
    [viewToken]
  )

  if (load.status === 'loading') {
    return (
      <main className='profile profile--center'>
        <div className='profile__spinner' role='status'>
          <span className='visually-hidden'>Loading profile</span>
        </div>
      </main>
    )
  }

  if (load.status === 'error') {
    return <ProfileNotice reason={load.reason} />
  }

  return (
    <ReportView
      profile={load.profile}
      fetchRegionalTrend={fetchRegionalTrend}
    />
  )
}

/** The report surface. Owns the shared interactive state (scan/metric/region).
 * The region-trend fetcher is injected so the recipient view and the owner
 * preview reuse this exact path. Exported for render tests. */
export function ReportView({
  profile,
  fetchRegionalTrend,
  includes,
}: {
  profile: APISharedProfileResponse
  fetchRegionalTrend: (
    techName: TechName
  ) => Promise<APIShareRegionalTrendResponse>
  /**
   * Owner-mode override for the control bar. When present, the live toggles decide
   * what shows (over the FULL previewMine payload), so owner preview and recipient
   * view stay identical by construction. Absent for the recipient, which derives
   * visibility from the payload header + data presence.
   */
  includes?: { photos: boolean; metrics: boolean; routine: boolean }
}) {
  const { header } = profile
  const [state, actions] = useReportState(profile)
  const range = formatScanRange(header.firstScanAt, header.lastScanAt)
  const hasScans = header.scanCount > 0 && state.scans.length > 0
  // Routine and the change log are independent of scans — a routine-only share
  // (no photos, no metrics) carries no scan series but still has a routine to
  // show. RoutineSection / ChangeLogSection each self-hide when empty.
  const hasRoutine = profile.routine.length > 0
  const hasMarkers = profile.markers.length > 0
  // Photos and Metrics are independent sections. The owner control bar overrides
  // via `includes`; the recipient derives from the header. Region dots need BOTH.
  const vis = includes
    ? {
        photos: includes.photos,
        metrics: includes.metrics,
        regionDots: includes.photos && includes.metrics,
      }
    : sectionVisibility(header)
  // Owner toggle hides routine + change log together; the recipient always renders
  // them (each self-hides when empty).
  const showRoutine = includes ? includes.routine : true
  // "Effective shared scans": the owner holds the FULL previewMine payload (so
  // hasScans is always true), but a toggle can hide every scan surface. A recipient
  // of the same config gets no scan series at all when both photos+metrics are off
  // (backend returns metrics:[]), so hasScans would be false for them. Gate ALL
  // scan-derived UI — the tracked/scans meta line, the player, and the empty state —
  // on this, so the owner preview matches the recipient exactly. For a recipient,
  // showPlayer === hasScans (they never have hasScans true with both flags off).
  const showPlayer = hasScans && (vis.photos || vis.metrics)
  // The "nothing yet" empty state only for a genuinely empty profile — not a
  // routine-only share, which has content below.
  const isEmpty = !showPlayer && !hasRoutine && !hasMarkers

  // Lazily pull the per-region trend for the current metric so region dots show
  // on EVERY scan (getProfile only carries them on the latest). Only when BOTH
  // photos and metrics are shared — the dots overlay the photo and read a metric
  // score. Failure degrades to no dots.
  const trend = useRegionalTrend(
    state.metric,
    vis.regionDots,
    fetchRegionalTrend
  )
  const regionalDots = useMemo(() => regionalByScanId(trend), [trend])

  return (
    <main className='profile'>
      {/* Brand masthead, then the patient identity as one left-aligned block —
          identity leads, since a provider reads this to know whose skin it is. */}
      <div className='profile__masthead'>
        <BrandMark />
      </div>
      <header className='profile__id'>
        <h1 className='profile__name'>{displayName(header.firstName)}</h1>
        {showPlayer && (
          <p className='profile__meta'>
            {plural(header.trackingWeeks, 'week')} tracked ·{' '}
            {plural(header.scanCount, 'scan')}
            {range ? ` · ${range}` : ''}
          </p>
        )}
        <div className='profile__pills'>
          <span className='pill pill--live'>
            <span className='pill__dot' /> Live view
          </span>
          <span className='pill pill--note'>
            Wellness tracking, not a diagnosis
          </span>
        </div>
      </header>

      {showPlayer && (
        <>
          {vis.metrics && (
            <MetricSwitcher profile={profile} state={state} actions={actions} />
          )}
          <ScanPlayer
            profile={profile}
            state={state}
            actions={actions}
            regionalDots={regionalDots}
            vis={vis}
          />
          {vis.metrics && (
            <MetricDefinition profile={profile} techName={state.metric} />
          )}
        </>
      )}

      {/* Routine + change log sit OUTSIDE the scan gate — a routine-only link
          still shows them. Each self-hides when its data is empty; the owner
          toggle (showRoutine) hides both regardless of data. */}
      {showRoutine && (
        <>
          <RoutineSection profile={profile} />
          <ChangeLogSection profile={profile} />
        </>
      )}

      {isEmpty && (
        <section className='profile__empty'>
          <p className='profile__empty-title'>Still building a picture</p>
          <p className='profile__empty-sub'>
            This profile does not have anything to show yet. Check back after
            the next scan.
          </p>
        </section>
      )}

      {/* Provider context + scope. Generic framing: no booking/provider/derm
          language, no gendered pronouns, forward-looking. Mirrors the mockup's
          closing note so a first-time viewer understands what they are seeing. */}
      <footer className='profile__foot'>
        <p>
          <b>How this was shared.</b> This link was created in the Stella app
          and shared with you directly. It is a live view, so it always reflects
          the most recent scans. What is shown can be changed and access revoked
          at any time, and the sender can see when it was last opened.
        </p>
        <p>
          <b>Scope.</b> Stella measures the visible appearance of skin from a
          phone photo. It is a wellness tracking tool, not a diagnostic device,
          and presents measurements without clinical interpretation.
        </p>
      </footer>
    </main>
  )
}
